import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Eye, Loader2, Megaphone, Power, Save, Wifi, WifiOff, X } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { api, apiError } from "@/lib/api";
import { useSiteStatus } from "@/context/SiteStatusContext";
import { AnnouncementBannerView } from "@/components/AnnouncementBanner";
import MaintenancePage from "@/components/MaintenancePage";
import { AdminCard, Field, inputCls, textareaCls } from "@/pages/admin/ui";

const THEMES = [
  { id: "blue", label: "Bleu ISMO", cls: "from-[#0b1f5c] via-[#0052ff] to-[#0891b2]" },
  { id: "amber", label: "Ambre", cls: "from-[#78350f] via-[#f59e0b] to-[#fbbf24]" },
  { id: "rose", label: "Rouge", cls: "from-[#4c0519] via-[#e11d48] to-[#fb7185]" },
  { id: "emerald", label: "Vert", cls: "from-[#064e3b] via-[#059669] to-[#34d399]" },
  { id: "violet", label: "Violet", cls: "from-[#2e1065] via-[#7c3aed] to-[#a78bfa]" },
  { id: "dark", label: "Nuit", cls: "from-[#020617] via-[#0f172a] to-[#1e293b]" },
];

const toLocalInput = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const off = d.getTimezoneOffset();
  return new Date(d.getTime() - off * 60000).toISOString().slice(0, 16);
};

export default function AdminSiteStatus() {
  const { refresh } = useSiteStatus();
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(false);

  useEffect(() => {
    api.get("/admin/site-settings").then(({ data }) => setSettings(data)).catch((e) => toast.error(apiError(e)));
  }, []);

  const setM = (k, v) => setSettings((s) => ({ ...s, maintenance: { ...s.maintenance, [k]: v } }));
  const setB = (k, v) => setSettings((s) => ({ ...s, banner: { ...s.banner, [k]: v } }));

  const save = async (override) => {
    const payload = override || settings;
    setSaving(true);
    try {
      const { data } = await api.put("/admin/site-settings", payload);
      setSettings(data);
      refresh();
      toast.success(data.maintenance.enabled ? "Site mis HORS LIGNE (mode maintenance actif)" : "Réglages enregistrés");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  if (!settings) return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-brand" /></div>;
  const m = settings.maintenance;
  const b = settings.banner;

  return (
    <div className="space-y-6" data-testid="admin-site-status-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl font-extrabold text-ink">Maintenance & annonces</h1>
          <p className="mt-1 text-sm text-slate-500">Mettez le site hors ligne pour les clients et revendeurs, ou affichez un message animé en haut du site sans le bloquer.</p>
        </div>
        <button onClick={() => save()} disabled={saving} data-testid="site-status-save"
          className="flex items-center gap-2 rounded-full bg-ink px-6 py-3 text-sm font-bold text-white transition-colors hover:bg-brand disabled:opacity-60">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Enregistrer
        </button>
      </div>

      {/* ---------------- Statut global ---------------- */}
      <section className={`relative overflow-hidden rounded-3xl border p-6 text-white transition-colors duration-500 ${m.enabled ? "border-rose-400/40 bg-[#1a0b14]" : "border-emerald-400/30 bg-[#04121b]"}`} data-testid="site-status-hero">
        <div className={`pointer-events-none absolute -right-20 -top-24 h-72 w-72 rounded-full blur-3xl ${m.enabled ? "bg-rose-600/40" : "bg-emerald-500/30"}`} />
        <div className="relative flex flex-wrap items-center justify-between gap-6">
          <div className="flex items-center gap-4">
            <span className={`relative flex h-16 w-16 items-center justify-center rounded-2xl ${m.enabled ? "bg-rose-500/20 text-rose-300" : "bg-emerald-500/20 text-emerald-300"}`}>
              {m.enabled ? <WifiOff className="h-7 w-7" /> : <Wifi className="h-7 w-7" />}
              <span className={`absolute -right-1 -top-1 h-3.5 w-3.5 rounded-full ${m.enabled ? "bg-rose-400" : "bg-emerald-400 animate-pulse"}`} />
            </span>
            <div>
              <p className="text-[11px] font-black uppercase tracking-[0.25em] text-white/60">État du site</p>
              <p className="font-display text-2xl font-black" data-testid="site-status-label">{m.enabled ? "HORS LIGNE — maintenance" : "EN LIGNE — accessible à tous"}</p>
              <p className="mt-1 text-xs text-white/60">{m.enabled ? "Seuls les administrateurs connectés peuvent naviguer. Clients et revendeurs voient la page de maintenance." : "Clients, revendeurs et visiteurs accèdent normalement au site."}</p>
            </div>
          </div>
          <button type="button" data-testid="maintenance-toggle" disabled={saving}
            onClick={() => save({ ...settings, maintenance: { ...m, enabled: !m.enabled } })}
            className={`flex items-center gap-3 rounded-full px-6 py-3.5 text-sm font-black shadow-lg transition-transform hover:-translate-y-0.5 ${m.enabled ? "bg-emerald-500 text-white shadow-emerald-500/30" : "bg-rose-600 text-white shadow-rose-600/30"}`}>
            <Power className="h-4 w-4" /> {m.enabled ? "Remettre le site EN LIGNE" : "Mettre le site HORS LIGNE"}
          </button>
        </div>
      </section>

      <div className="grid gap-6 xl:grid-cols-2">
        {/* ---------------- Maintenance ---------------- */}
        <AdminCard title="Page de maintenance" description="Contenu affiché aux clients et revendeurs lorsque le site est hors ligne."
          action={
            <button type="button" onClick={() => setPreview(true)} data-testid="maintenance-preview-btn"
              className="flex items-center gap-1.5 rounded-full border border-line px-3.5 py-2 text-xs font-bold text-ink hover:border-brand hover:text-brand">
              <Eye className="h-3.5 w-3.5" /> Aperçu plein écran
            </button>
          }>
          <div className="space-y-4">
            <div className="flex items-center justify-between rounded-xl border border-line bg-surface px-4 py-3">
              <div>
                <p className="text-sm font-bold text-ink">Mode maintenance</p>
                <p className="text-xs text-slate-500">Bloque tout le site sauf la page de connexion et l'administration.</p>
              </div>
              <Switch checked={m.enabled} onCheckedChange={(v) => setM("enabled", v)} data-testid="maintenance-switch" />
            </div>
            <Field label="Titre"><input className={inputCls} value={m.title} onChange={(e) => setM("title", e.target.value)} data-testid="maintenance-title-input" /></Field>
            <Field label="Message">
              <textarea rows={4} className={textareaCls} value={m.message} onChange={(e) => setM("message", e.target.value)} data-testid="maintenance-message-input" />
            </Field>
            <Field label="Retour prévu (texte libre, optionnel)" hint="Ex : « Retour prévu aujourd'hui à 18h00 »">
              <input className={inputCls} value={m.eta} onChange={(e) => setM("eta", e.target.value)} data-testid="maintenance-eta-input" />
            </Field>
            <Field label="Compte à rebours jusqu'à (optionnel)" hint="Un compte à rebours en direct s'affiche sur la page de maintenance jusqu'à cette date et heure.">
              <div className="flex gap-2">
                <input type="datetime-local" className={inputCls} value={toLocalInput(m.eta_at)} onChange={(e) => setM("eta_at", e.target.value ? new Date(e.target.value).toISOString() : "")} data-testid="maintenance-eta-at-input" />
                {m.eta_at && <button type="button" onClick={() => setM("eta_at", "")} className="shrink-0 rounded-xl border border-line px-3 text-xs font-bold text-slate-500 hover:text-rose-600" data-testid="maintenance-eta-at-clear">Effacer</button>}
              </div>
            </Field>
            <label className="flex items-center justify-between rounded-xl border border-line px-4 py-3 text-sm">
              <span className="font-semibold text-ink">Afficher le bouton « Nous contacter sur WhatsApp »</span>
              <Switch checked={m.show_contact} onCheckedChange={(v) => setM("show_contact", v)} data-testid="maintenance-contact-switch" />
            </label>
          </div>
        </AdminCard>

        {/* ---------------- Bandeau ---------------- */}
        <AdminCard title="Message animé en haut du site" description="S'affiche au-dessus de l'en-tête, même lorsque le site est en ligne. Ne bloque rien.">
          <div className="space-y-4">
            <div className="flex items-center justify-between rounded-xl border border-line bg-surface px-4 py-3">
              <div className="flex items-center gap-2">
                <Megaphone className="h-4 w-4 text-brand" />
                <div>
                  <p className="text-sm font-bold text-ink">Activer le bandeau</p>
                  <p className="text-xs text-slate-500">Visible par tous les visiteurs, clients et revendeurs.</p>
                </div>
              </div>
              <Switch checked={b.enabled} onCheckedChange={(v) => setB("enabled", v)} data-testid="banner-switch" />
            </div>
            <Field label="Texte du message">
              <textarea rows={2} className={textareaCls} value={b.text} onChange={(e) => setB("text", e.target.value)} data-testid="banner-text-input" />
            </Field>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Style d'animation">
                <div className="grid grid-cols-2 gap-2">
                  {[["marquee", "Texte défilant"], ["glow", "Fixe & lumineux"]].map(([id, label]) => (
                    <button key={id} type="button" onClick={() => setB("style", id)} data-testid={`banner-style-${id}`}
                      className={`rounded-xl border px-3 py-2.5 text-xs font-bold transition-colors ${b.style === id ? "border-brand bg-brand-light text-brand" : "border-line text-slate-600 hover:border-slate-300"}`}>
                      {label}
                    </button>
                  ))}
                </div>
              </Field>
              <Field label={`Vitesse de défilement — ${b.speed}s / cycle`} hint="Plus la valeur est petite, plus le texte défile vite.">
                <input type="range" min={8} max={120} value={b.speed} onChange={(e) => setB("speed", Number(e.target.value))} disabled={b.style !== "marquee"}
                  className="mt-3 w-full accent-brand disabled:opacity-40" data-testid="banner-speed-input" />
              </Field>
            </div>
            <Field label="Couleur">
              <div className="flex flex-wrap gap-2">
                {THEMES.map((t) => (
                  <button key={t.id} type="button" title={t.label} onClick={() => setB("theme", t.id)} data-testid={`banner-theme-${t.id}`}
                    className={`h-9 w-9 rounded-full bg-gradient-to-r ${t.cls} ring-offset-2 transition-transform hover:scale-110 ${b.theme === t.id ? "ring-2 ring-ink" : ""}`} />
                ))}
              </div>
            </Field>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Bouton (optionnel) — texte"><input className={inputCls} value={b.link_label} onChange={(e) => setB("link_label", e.target.value)} placeholder="En savoir plus" data-testid="banner-link-label-input" /></Field>
              <Field label="Bouton — lien"><input className={inputCls} value={b.link_url} onChange={(e) => setB("link_url", e.target.value)} placeholder="/support ou https://…" data-testid="banner-link-url-input" /></Field>
            </div>
            <label className="flex items-center justify-between rounded-xl border border-line px-4 py-3 text-sm">
              <span className="font-semibold text-ink">Permettre aux visiteurs de fermer le bandeau</span>
              <Switch checked={b.closable} onCheckedChange={(v) => setB("closable", v)} data-testid="banner-closable-switch" />
            </label>
            <div>
              <p className="mb-2 text-[11px] font-bold uppercase tracking-wide text-slate-500">Aperçu en direct</p>
              {b.text?.trim() ? <AnnouncementBannerView banner={{ ...b, enabled: true }} preview /> : <p className="text-xs text-slate-400">Saisissez un texte pour voir l'aperçu.</p>}
            </div>
          </div>
        </AdminCard>
      </div>

      {preview && (
        <div className="fixed inset-0 z-[9995]" data-testid="maintenance-preview-overlay">
          <MaintenancePage data={m} preview />
          <button type="button" onClick={() => setPreview(false)} data-testid="maintenance-preview-close"
            className="absolute right-5 top-5 z-[9999] flex items-center gap-2 rounded-full bg-white/10 px-4 py-2 text-xs font-bold text-white backdrop-blur hover:bg-white/20">
            <X className="h-4 w-4" /> Fermer l'aperçu
          </button>
        </div>
      )}
    </div>
  );
}
